import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementSupporttypStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {SupportTyp} from '../../../types/support-typ';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-supporttyp-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './supporttyp-input.component.html',
    styleUrls: ['./supporttyp-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupporttypInputComponent {

  @Input() readonly = false;

  readonly typInEditing$ = new BehaviorSubject<SupportTyp | null>(null);

  readonly entities$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP,
    HardwareManagementSupporttypStore.getters.all
  ).pipe(map(o => o as SupportTyp[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly typEditingForm = new UntypedFormGroup({
    typ: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(typ: SupportTyp | null | undefined): void {
    if(!typ)
      return;
    this.formGroup.patchValue({ supporttyp: typ });
  }

  edit(entity: SupportTyp | null | undefined): void {
    if(!entity)
      return;
    this.typInEditing$.next(entity);
    this.typEditingForm.patchValue(entity);
  }

  resetForm(typ?: SupportTyp | null): void {
    if (!typ) {
      this.typEditingForm.reset();
      return;
    }

    this.typEditingForm.reset(typ);
  }

  async saveEdit(entity: SupportTyp): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.typEditingForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP,
        HardwareManagementSupporttypStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.typEditingForm.reset();
      this.typInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Support-Typ wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async create(entity: SupportTyp): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_SUPPORTTYP,
        HardwareManagementSupporttypStore.actions.create,
        { entity }
      );

      this.select(result);
      this.typEditingForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Support-Typ wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: SupportTyp | null | undefined): void {
    if (!newValue) {
      this.typEditingForm.reset();
      this.typInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.supporttyp?.id !== newValue?.id) {
      this.formGroup.patchValue({ supporttyp: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ supporttyp: null });
    this.typEditingForm.reset();
    this.typInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.typEditingForm.reset();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterTypes = (types: SupportTyp[], filter: string): SupportTyp[] => {
    if (!filter || filter?.length < 1) {
      return types;
    }

    return types.filter(typ => {
      return typ.typ.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
